import { useSelector } from 'react-redux';
import { BellNotificationIcon, BellSlashIcon } from '@rsos/assets/icons';
import { ConditionalDisplay } from '@rsos/base-components';
import { selectCapstoneAudioSettingsForFeature } from '@rsos/sinatra/src/selectors/selectAudioSettings';
import { selectIsInteragencyChatBannerEnabled } from '@rsos/sinatra/src/selectors/selectInteragencyChatNotifications';
import { useInteragencyConversationSubscription } from '../../../reduxDataStore/interagency/interagencyChatSubscriptions';
import { useSelectedInteragencyChat } from '../../../reduxDataStore/interagency/interagencySlice';
import styles from './SubscriptionToggle.module.css';

const SUBSCRIBED_TOOLTIP = "You're subscribed to this conversation";
const MUTED_TOOLTIP = 'Notifications muted';

/**
 * Subscribes the user to the open conversation, or mutes it. Subscription is
 * what gates banners and audio for new messages; muting leaves the unread dot
 * and the tab badge alone.
 *
 * Hidden unless the agency has a notification type on — banners or sound.
 * Subscription gates both, so either being on is reason to offer the control.
 */
function SubscriptionToggle() {
  const selectedConversation = useSelectedInteragencyChat();
  const isBannerEnabledForAgency = useSelector(
    selectIsInteragencyChatBannerEnabled,
  );
  const { sound_on: isSoundEnabledForAgency } = useSelector(
    selectCapstoneAudioSettingsForFeature('interagency_chat'),
  );
  const { isSubscribed, isLoading, subscribe, unsubscribe } =
    useInteragencyConversationSubscription(selectedConversation);

  const handleToggleSubscription = async () => {
    if (!selectedConversation) return;

    try {
      if (isSubscribed) {
        await unsubscribe(selectedConversation);
      } else {
        await subscribe(selectedConversation);
      }
    } catch (err: unknown) {
      // The bell renders from stored state rather than an optimistic guess,
      // so a rejected write leaves it showing what is actually stored.
      // eslint-disable-next-line no-console -- surfaced to Sentry
      console.error('Failed to change interagency chat subscription:', err);
    }
  };

  const tooltip = isSubscribed ? SUBSCRIBED_TOOLTIP : MUTED_TOOLTIP;

  return (
    <ConditionalDisplay
      dataName="interagency-chat-subscription-toggle"
      conditions={[
        {
          showIf: isBannerEnabledForAgency || isSoundEnabledForAgency,
          reason:
            'Agency has both Notification Banners and Sound off (Admin > Notifications > Agency Conversations)',
        },
        {
          showIf: !!selectedConversation,
          reason: 'No conversation is open',
        },
        {
          // Showing a bell before the subscription list has been fetched
          // would show the wrong one half the time.
          showIf: !isLoading,
          reason: 'Subscription state is still loading',
        },
      ]}
    >
      <button
        type="button"
        className={styles.subscriptionToggle}
        onClick={handleToggleSubscription}
        title={tooltip}
        aria-label={tooltip}
        aria-pressed={isSubscribed}
        data-name="interagency-chat-subscription-toggle"
      >
        {isSubscribed ? (
          <BellNotificationIcon width={16} height={16} />
        ) : (
          <BellSlashIcon width={16} height={16} />
        )}
      </button>
    </ConditionalDisplay>
  );
}

export default SubscriptionToggle;
